// src/components/dashboard/Benchmarks.js
import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useBenchmarksData } from "@/hooks/post-analysis/useBenchmarksData";
import MetricCard from "../ui/MetricCard";
import BenchmarkSkeleton from "../ui/BenchmarkSkeleton";
import { formatValue } from "@/lib/post_analysis_utils/formatValue";
import { exportToCSV } from "@/lib/post_analysis_utils/exportToCSV";
import { BENCHMARK_COLUMNS } from "../tables/table_columns";
import UnifiedTable from "../tables/UnifiedTable";
import BenchmarkCharts from "../charts/BenchmarkCharts";
import TableSkeleton from "../ui/TableSkeleton";
import BenchmarkDeviationTable from "../tables/BenchmarkDeviationTable";
import { usePostAnalysisFilters } from "@/hooks/post-analysis/usePostAnalysisFilters";
import PostAnalysisHeaderWithFilters from "../ui/PostAnalysisHeaderWithFilters";

export default function DashboardBenchmarks({ filters, setFilters }) {
  const [showFilters, setShowFilters] = useState(false);
  const [selectedMetric, setSelectedMetric] = useState("impressions");

  const { 
    data, 
    isLoading, 
    error, 
    filterOptions, 
    metricOptions, 
    getMetricLabel, 
    getMetricType, 
    page, 
    setPage, 
    pageSize, 
    setPageSize, 
    totalRows, 
    totalPages, 
    isTableLoading, 
    getAllBenchmarksData
  } = useBenchmarksData({
    region: filters.region,
    brand: filters.brand,
    campaign_name: filters.campaign,
    start_date: filters.start_date,
    end_date: filters.end_date,
    poc_id: filters.poc_id,
    poc_name: filters.poc_name,
    key_account: filters.key_account
  });

  // Build chart metric options from backend - memoized to prevent recreation
  const chartMetricOptions = useMemo(() => 
    metricOptions?.y_axis || [], 
    [metricOptions?.y_axis]
  );

  // Shared local filter options + change handler
  const { localFilterOptions, handleLocalFiltersChange } = usePostAnalysisFilters(
    filterOptions || {}
  );

  // Set default selected metric when data loads
  useEffect(() => {
    if (chartMetricOptions.length > 0 && !selectedMetric) {
      setSelectedMetric(chartMetricOptions[0].value);
    }
  }, [chartMetricOptions, selectedMetric]);

  // Reset page to 1 when filters change - memoized to prevent unnecessary calls
  const filtersString = useMemo(() => JSON.stringify(filters), [filters]);
  useEffect(() => {
    // Only reset page when filters actually change, not when page changes
    setPage(1);
  }, [filtersString, setPage]);

  // Handle clear all filters - memoized with stable dependencies
  const handleClearAll = useCallback(() => {
    setFilters({ 
      brand: "", 
      region: "", 
      campaign: [], 
      start_date: "", 
      end_date: "", 
      poc_id: [], 
      poc_name: [],
      key_account: ""
    });
    setPage(1);
  }, [setFilters, setPage]);

  // Prepare metric cards data with proper formatting - memoized to prevent recreation
  const metricCards = useMemo(() => {
    if (!data?.metrics) return [];
    return Object.entries(data.metrics).map(([key, metric]) => (
      <MetricCard
        key={key}
        title={getMetricLabel(key, metricOptions)}
        value={formatValue(metric.mean || 0, { type: getMetricType(key, metricOptions) })}
        subtitle={""}
      />
    ));
  }, [data?.metrics, metricOptions]); // Removed getMetricLabel and getMetricType from deps as they're stable functions

  // Prepare chart data with type information - memoized to prevent recreation
  const brandChartData = useMemo(() => {
    const chartData = data?.brandChart || data?.brand_chart || [];
    return chartData.map((row) => ({
      label: row.brand || "—",
      value: row[selectedMetric] != null ? row[selectedMetric] : 0,
      type: getMetricType(selectedMetric, metricOptions)
    }));
  }, [data?.brandChart, data?.brand_chart, selectedMetric, metricOptions]);

  const regionChartData = useMemo(() => {
    const chartData = data?.regionChart || data?.region_chart || [];
    return chartData.map((row) => ({
      label: row.region || "—",
      value: row[selectedMetric] != null ? row[selectedMetric] : 0,
      type: getMetricType(selectedMetric, metricOptions)
    }));
  }, [data?.regionChart, data?.region_chart, selectedMetric, metricOptions]);

  // Prepare deviation table data with type information - memoized to prevent recreation
  const deviationTable = useMemo(() => {
    const tableData = data?.deviationTable || data?.deviation_table || [];
    return tableData.map(row => {
      const metricData = {};
      chartMetricOptions.forEach(opt => {
        metricData[opt.value] = row[opt.value];
        metricData[`${opt.value}_deviation`] = row[`${opt.value}_deviation`];
      });
      return {
        poc_id: row.poc_id,
        poc_name: row.poc_name,
        campaign_name: row.campaign_name,
        brand: row.brand,
        region: row.region,
        ...metricData,
        avg_deviation: row.avg_deviation
      };
    });
  }, [data?.deviationTable, data?.deviation_table, chartMetricOptions]);

  // Create metric types mapping from backend data - memoized to prevent recreation
  const metricTypes = useMemo(() => 
    Object.fromEntries(chartMetricOptions.map(opt => [opt.value, opt.type])),
    [chartMetricOptions]
  );

  // Export all benchmarks data to CSV - memoized to prevent recreation
  const handleExportAllCSV = useCallback(async () => {
    try {
      const allData = await getAllBenchmarksData();
      const rows = allData.deviation_table || [];
      exportToCSV(rows, "benchmarks_table.csv");
    } catch (err) {
      alert("Failed to export all data. Please try again.");
    }
  }, [getAllBenchmarksData]);

  if (isLoading) return <BenchmarkSkeleton />;
  if (error) return <div className="text-red-500">Error: {error.message}</div>;
  if (!data) return null;

  return (
    <div className="p-6 space-y-8">
      <PostAnalysisHeaderWithFilters
        title="Benchmarks"
        subtitle="Displaying data as per the selected filters"
        filters={filters}
        setFilters={setFilters}
        baseFilterOptions={filterOptions}
        localFilterOptions={localFilterOptions}
        handleLocalFiltersChange={handleLocalFiltersChange}
      />

      {/* Metric Cards */}
      <div className="grid grid-cols-4 gap-4">{metricCards}</div>

      {/* Charts */}
      <BenchmarkCharts
        brandChartData={brandChartData}
        regionChartData={regionChartData}
        selectedMetric={selectedMetric}
        onMetricChange={setSelectedMetric}
        chartMetricOptions={chartMetricOptions}
      />
      
      {/* Deviation Table */}
      <div className="bg-background text-foreground border border-border rounded-lg shadow p-4 mt-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-foreground">Most Deviated POCs</h2>
          {data?.deviation_table?.length > 0 && (
            <button
              className="px-3 py-1 rounded bg-primary text-primary-foreground hover:bg-primary/90 text-sm"
              onClick={handleExportAllCSV}
            >
              Export CSV
            </button>
          )}
        </div>
        {isTableLoading ? (
          <TableSkeleton />
        ) : (
          <BenchmarkDeviationTable data={deviationTable} metricTypes={metricTypes} />
        )}
        <div className="flex items-center justify-between mt-2">
          <div>
            Page {page} of {totalPages}
          </div>
          <div className="flex gap-2">
            <button
              className="px-2 py-1 rounded border text-sm disabled:opacity-50"
              onClick={() => setPage(page - 1)}
              disabled={page === 1}
            >
              Previous
            </button>
            <button
              className="px-2 py-1 rounded border text-sm disabled:opacity-50"
              onClick={() => setPage(page + 1)}
              disabled={page === totalPages}
            >
              Next
            </button>
          </div>
          <div>
            Rows per page:
            <select
              className="ml-2 border rounded px-1 py-0.5 text-sm"
              value={pageSize}
              onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }}
            >
              {[10, 20, 50, 100].map(size => (
                <option key={size} value={size}>{size}</option>
              ))}
            </select>
          </div>
        </div>
      </div>
    </div>
  );
}